/**
 * TemplateEditorPage
 *
 * Full-page TipTap contract editor with:
 *  • Formatting toolbar (bold/italic/underline, headings, align, lists, table, image, undo/redo, page break)
 *  • A4-proportioned white document canvas (Google Docs aesthetic)
 *  • Right sidebar: insert field chips + live field list
 *  • Library selection modal on first open (new templates only)
 *  • Preview (generates PDF via backend, shows in modal)
 *  • Save (creates or updates editor-based template)
 *
 * Routes:
 *   /templates/editor/new?name=...&description=...&trigger=...&slug=...
 *   /templates/editor/:id/edit
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useEditor, useEditorState, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Image from '@tiptap/extension-image';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Extension } from '@tiptap/core';
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  Divider,
  InlineStack,
  Modal,
  Page,
  Select,
  Spinner,
  Text,
} from '@shopify/polaris';
import { Document, Page as PdfPage } from 'react-pdf';
import { useApi } from '../hooks/useApi';
import { signerOptions } from '../multiSigner';
import type { SignerOption, TemplateSigner } from '../types';
import { SignatureFieldNode, FIELD_COLORS, insertSignatureField } from '../components/editor/SignatureFieldNode';
import { PageBreakNode } from '../components/editor/PageBreakNode';
import { ConditionalSectionNode } from '../components/editor/ConditionalSectionNode';
import { checkboxesIn } from '../components/editor/checkboxes';

// ---------------------------------------------------------------------------
// FontSize extension (inline — no separate package needed)
// ---------------------------------------------------------------------------

const FontSize = Extension.create({
  name: 'fontSize',
  addGlobalAttributes() {
    return [{
      types: ['textStyle'],
      attributes: {
        fontSize: {
          default: null,
          parseHTML: (el: HTMLElement) => el.style.fontSize || null,
          renderHTML: (attrs: Record<string, any>) => {
            if (!attrs.fontSize) return {};
            return { style: `font-size: ${attrs.fontSize}` };
          },
        },
      },
    }];
  },
  addCommands() {
    return {
      setFontSize: (size: string) => ({ chain }: any) =>
        chain().setMark('textStyle', { fontSize: size }).run(),
      unsetFontSize: () => ({ chain }: any) =>
        chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run(),
    } as any;
  },
});

// ---------------------------------------------------------------------------
// Editor CSS
// ---------------------------------------------------------------------------

const EDITOR_CSS = `
.ProseMirror { outline: none; min-height: 900px; padding: 48px 56px; font-size: 13.5px; line-height: 1.7; color: #111; }
.ProseMirror h1 { font-size: 22px; font-weight: 700; margin: 0 0 12px; }
.ProseMirror h2 { font-size: 18px; font-weight: 600; margin: 0 0 10px; }
.ProseMirror h3 { font-size: 15px; font-weight: 600; margin: 0 0 8px; }
.ProseMirror p  { margin: 0 0 8px; }
.ProseMirror ul, .ProseMirror ol { padding-left: 22px; margin: 0 0 8px; }
.ProseMirror table { width: 100%; border-collapse: collapse; margin: 0 0 12px; }
.ProseMirror td, .ProseMirror th { border: 1px solid #ccc; padding: 5px 8px; min-width: 40px; }
.ProseMirror th { background: #f3f4f6; font-weight: 600; }
.ProseMirror img { max-width: 100%; border-radius: 4px; }
.ProseMirror .selectedCell:after { z-index: 2; position: absolute; content: ""; left: 0; right: 0; top: 0; bottom: 0; background: rgba(37,99,235,.08); pointer-events: none; }
.ProseMirror .page-break-bar { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin: 18px 0; padding: 4px 6px 4px 10px; border: 1px dashed #9ca3af; border-radius: 6px; background: #f9fafb; font-size: 11px; color: #6b7280; user-select: none; }
.ProseMirror .page-break-bar button { border: 1px solid #d1d5db; background: #fff; border-radius: 5px; padding: 2px 8px; font-size: 11px; color: #b91c1c; cursor: pointer; }
.ProseMirror .page-break-bar button:hover { background: #fef2f2; border-color: #fca5a5; }
.ProseMirror .cond-section-box { border: 2px dashed #a78bfa; border-radius: 8px; padding: 0 10px 4px; margin: 12px 0; background: #faf5ff; }
.ProseMirror .cond-section-box.cond-section-unset { border-color: #f59e0b; background: #fffbeb; }
.ProseMirror .cond-section-head { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; padding: 6px 0; margin-bottom: 4px; border-bottom: 1px solid #e9d5ff; font-size: 11px; color: #6d28d9; user-select: none; }
.ProseMirror .cond-section-title { font-weight: 600; }
.ProseMirror .cond-section-head select { height: 24px; font-size: 11px; border: 1px solid #ddd6fe; border-radius: 4px; max-width: 300px; }
.ProseMirror .cond-section-head button { margin-left: auto; border: 1px solid #ddd6fe; background: #fff; border-radius: 5px; padding: 2px 8px; font-size: 11px; color: #6d28d9; cursor: pointer; }
.ProseMirror .cond-section-hint { font-size: 11px; color: #b45309; margin-bottom: 4px; user-select: none; }
.ProseMirror .page-break-bar.ProseMirror-selectednode { outline: 2px solid #2563eb; outline-offset: 1px; }
.ProseMirror p.is-editor-empty:first-child::before { color: #adb5bd; content: attr(data-placeholder); float: left; height: 0; pointer-events: none; }
`;

function injectEditorCss() {
  if (document.getElementById('tiptap-css')) return;
  const s = document.createElement('style');
  s.id = 'tiptap-css';
  s.textContent = EDITOR_CSS;
  document.head.appendChild(s);
}

// ---------------------------------------------------------------------------
// Toolbar button
// ---------------------------------------------------------------------------

function TB({
  active, disabled, onClick, title, children,
}: {
  active?: boolean; disabled?: boolean; onClick: () => void; title: string; children: React.ReactNode;
}) {
  return (
    <button
      title={title}
      disabled={disabled}
      onClick={onClick}
      style={{
        padding: '3px 8px',
        height: '28px',
        border: 'none',
        borderRadius: '4px',
        cursor: disabled ? 'default' : 'pointer',
        background: active ? '#dbeafe' : 'transparent',
        color: active ? '#1d4ed8' : disabled ? '#d1d5db' : '#374151',
        fontSize: '13px',
        fontWeight: active ? 700 : 500,
        lineHeight: '22px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '28px',
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      {children}
    </button>
  );
}

function Sep() {
  return <div style={{ width: 1, background: '#d1d5db', height: 24, margin: '0 4px', flexShrink: 0, alignSelf: 'center' }} />;
}

/** Labelled button for the table tools row. */
function TT({
  onClick, disabled, danger, active, title, children,
}: {
  onClick: () => void; disabled?: boolean; danger?: boolean; active?: boolean; title?: string; children: React.ReactNode;
}) {
  return (
    <button
      title={title}
      disabled={disabled}
      aria-pressed={active}
      // Keep the cursor in the table: don't let the click steal the selection.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      style={{
        padding: '3px 8px', height: '26px', border: `1px solid ${active ? '#2563eb' : '#e5e7eb'}`, borderRadius: '4px',
        background: active ? '#eff6ff' : '#fff', cursor: disabled ? 'default' : 'pointer', whiteSpace: 'nowrap',
        color: disabled ? '#d1d5db' : danger ? '#b91c1c' : '#374151', fontSize: '12px', fontWeight: 500,
      }}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Field tools helpers
// ---------------------------------------------------------------------------

interface SelectedField {
  fieldId: string | null; fieldType: string; label: string;
  required: boolean; showIf: string | null; signerPosition: number;
  /** Inside a conditional section: with no own condition it follows the section. */
  inSection: boolean;
}

/** Field name box: renames the field when you press Enter or click away. */
function FieldNameInput({ value, onCommit }: { value: string; onCommit: (label: string) => void }) {
  const [text, setText] = useState(value);
  const commit = () => { const t = text.trim(); if (t && t !== value) onCommit(t.slice(0, 80)); else setText(value); };
  return (
    <input
      aria-label="Field name"
      title="Field name — shown to the signer and in error messages"
      value={text}
      maxLength={80}
      onChange={(e) => setText(e.target.value)}
      onFocus={(e) => e.target.select()}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
      style={{ height: 26, fontSize: 12, border: '1px solid #e5e7eb', borderRadius: 4, padding: '0 6px', width: 170 }}
    />
  );
}

// ---------------------------------------------------------------------------
// Library types
// ---------------------------------------------------------------------------

interface LibMeta { slug: string; name: string; description: string; fieldCount: number }

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function TemplateEditorPage() {
  injectEditorCss();
  const { id }           = useParams<{ id?: string }>();
  const [params]         = useSearchParams();
  const navigate         = useNavigate();
  const { request, upload, getToken } = useApi();
  const fileInputRef     = useRef<HTMLInputElement>(null);

  const isNew = !id;

  // Template metadata (from URL for new, from API for edit)
  const [templateName, setTemplateName]   = useState(params.get('name') ?? 'Untitled');
  const [description,  setDescription]   = useState(params.get('description') ?? '');
  const [triggerType,  setTriggerType]   = useState(params.get('trigger') ?? 'MANUAL');
  const [savedId,      setSavedId]       = useState<string | null>(id ?? null);

  // UI state
  const [saving,        setSaving]       = useState(false);
  const [previewing,    setPreviewing]   = useState(false);
  const [error,         setError]        = useState<string | null>(null);
  const [previewUrl,    setPreviewUrl]   = useState<string | null>(null);
  const [showPreview,   setShowPreview]  = useState(false);
  const [showLibrary,   setShowLibrary]  = useState(isNew && !params.get('slug'));
  const [library,       setLibrary]      = useState<LibMeta[]>([]);
  const [fieldCount,    setFieldCount]   = useState(0);
  const [numPreviewPages, setNumPreviewPages] = useState(1);
  // Multi-signer: the template's signers (customer first) and who new fields are for.
  const [signers,       setSigners]      = useState<SignerOption[]>(signerOptions(undefined));
  const [insertFor,     setInsertFor]    = useState(1);
  const insertForSigner = signers.find((s) => s.position === insertFor) ?? signers[0];

  // ── Unsaved work protection ────────────────────────────────────────────────
  // dirty: edits since the last save. A copy of unsaved work is kept in this
  // browser so it can be restored after closing the page by mistake.
  const [dirty, setDirty] = useState(false);
  const [draftFound, setDraftFound] = useState<{ content: object; at: number } | null>(null);
  const lastSavedRef = useRef<object | null>(null);
  const draftKey = savedId ? `signshield:editor-draft:${savedId}` : `signshield:editor-draft:new:${params.get('slug') ?? 'blank'}`;
  const draftKeyRef = useRef(draftKey);
  draftKeyRef.current = draftKey;
  const draftTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ---------------------------------------------------------------------------
  // TipTap editor
  // ---------------------------------------------------------------------------

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      FontSize,
      Color,
      // Size via the "Image size" buttons; the width is saved and used in the PDF.
      Image.configure({ inline: false }),
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      SignatureFieldNode,
      PageBreakNode,
      ConditionalSectionNode,
    ],
    content: '<p>Start writing your contract…</p>',
    onUpdate({ editor: ed }) {
      let count = 0;
      ed.state.doc.descendants((node) => {
        if (node.type.name === 'signatureField') count++;
      });
      setFieldCount(count);
      setDirty(true);
      // Keep a copy of unsaved work in this browser (debounced).
      if (draftTimer.current) clearTimeout(draftTimer.current);
      draftTimer.current = setTimeout(() => {
        try {
          localStorage.setItem(draftKeyRef.current, JSON.stringify({ content: ed.getJSON(), at: Date.now() }));
        } catch { /* storage unavailable: the save bar and leave warning still protect the work */ }
      }, 800);
    },
  });

  /** After the document has loaded: remember it as saved, and offer any newer unsaved copy. */
  const markLoaded = useCallback((content: object) => {
    if (draftTimer.current) clearTimeout(draftTimer.current);
    lastSavedRef.current = content;
    setDirty(false);
    try {
      const raw = localStorage.getItem(draftKeyRef.current);
      if (!raw) return;
      const draft = JSON.parse(raw) as { content: object; at: number };
      if (draft?.content && JSON.stringify(draft.content) !== JSON.stringify(content)) setDraftFound(draft);
      else localStorage.removeItem(draftKeyRef.current);
    } catch { /* ignore unreadable drafts */ }
  }, []);

  const clearDraft = useCallback(() => {
    if (draftTimer.current) clearTimeout(draftTimer.current);
    try { localStorage.removeItem(draftKeyRef.current); } catch { /* ignore */ }
    setDraftFound(null);
  }, []);

  // Browser close / reload with unsaved changes: the browser asks first.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  // Shopify's Save / Discard bar while there are unsaved changes (Shopify then
  // asks before navigating away inside the admin).
  useEffect(() => {
    const bar = (window as any).shopify?.saveBar;
    if (!bar) return;
    (dirty ? bar.show('editor-save-bar') : bar.hide('editor-save-bar'))?.catch?.(() => {});
  }, [dirty]);
  useEffect(() => () => { (window as any).shopify?.saveBar?.hide('editor-save-bar')?.catch?.(() => {}); }, []);

  // Table tools follow the cursor; image size tools appear when an image is selected.
  const inTable = useEditorState({ editor, selector: (ctx) => ctx.editor?.isActive('table') ?? false }) ?? false;
  const onImage = useEditorState({ editor, selector: (ctx) => ctx.editor?.isActive('image') ?? false }) ?? false;
  // The selected field (null when none) and the document's checkboxes it may depend on.
  const selectedField = useEditorState({
    editor,
    selector: (ctx): SelectedField | null => {
      if (!ctx.editor?.isActive('signatureField')) return null;
      const a = ctx.editor.getAttributes('signatureField');
      return {
        fieldId: a.fieldId ?? null, fieldType: a.fieldType, label: a.label ?? '',
        required: a.required !== false, showIf: a.showIf ?? null, signerPosition: a.signerPosition ?? 1,
        inSection: ctx.editor.isActive('conditionalSection'),
      };
    },
  }) ?? null;
  // A checkbox that switches a section or field on is always optional (the server enforces it too).
  const isSwitch = !!selectedField && selectedField.fieldType === 'CHECKBOX' && !!editor && (() => {
    let used = false;
    editor.state.doc.descendants((n: any) => {
      if (used) return false;
      if ((n.type.name === 'signatureField' || n.type.name === 'conditionalSection') && n.attrs.showIf && n.attrs.showIf === selectedField.fieldId) used = true;
    });
    return used;
  })();
  const checkboxChoices = selectedField && editor
    ? checkboxesIn(editor.state.doc, { excludeId: selectedField.fieldId, maxSigner: selectedField.signerPosition })
    : [];

  // ---------------------------------------------------------------------------
  // Load existing template content
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!id || !editor) return;
    (async () => {
      try {
        const content = await request<object | null>(`/templates/${id}/editor-content`);
        if (content) editor.commands.setContent(content);
        markLoaded(editor.getJSON());
        const tmpl = await request<{ name: string; description: string | null; triggerType: string; signers?: TemplateSigner[] }>(`/templates/${id}`);
        setTemplateName(tmpl.name);
        setDescription(tmpl.description ?? '');
        setTriggerType(tmpl.triggerType);
        setSigners(signerOptions(tmpl.signers));
      } catch (e: any) {
        setError(e.message);
      }
    })();
  }, [id, editor, request, markLoaded]);

  // Load library template from slug query param (new templates only)
  useEffect(() => {
    const slug = params.get('slug');
    if (!slug || !editor) return;
    request<object>(`/templates/library/${slug}`)
      .then((json) => { editor.commands.setContent(json); markLoaded(editor.getJSON()); })
      .catch(() => {});
  }, [params, editor, request, markLoaded]);

  // Blank new template: the starting text counts as saved (nothing to lose yet).
  useEffect(() => {
    if (!editor || id || params.get('slug')) return;
    markLoaded(editor.getJSON());
  }, [editor, id, params, markLoaded]);

  // ---------------------------------------------------------------------------
  // Library modal
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!showLibrary) return;
    request<LibMeta[]>('/templates/library').then(setLibrary).catch(() => {});
  }, [showLibrary, request]);

  const loadLibraryTemplate = async (slug: string) => {
    try {
      const json = await request<object>(`/templates/library/${slug}`);
      editor?.commands.setContent(json);
      if (editor) markLoaded(editor.getJSON());
      setShowLibrary(false);
    } catch (e: any) {
      setError(e.message);
    }
  };

  // ---------------------------------------------------------------------------
  // Save
  // ---------------------------------------------------------------------------

  const handleSave = useCallback(async () => {
    if (!editor) return;
    setSaving(true);
    setError(null);
    try {
      const editorContent = editor.getJSON();
      if (savedId) {
        await request(`/templates/${savedId}/editor`, {
          method: 'PUT',
          body: JSON.stringify({ editorContent }),
        });
        lastSavedRef.current = editorContent;
        setDirty(false);
        clearDraft();
      } else {
        const created = await request<{ id: string }>('/templates/editor', {
          method: 'POST',
          body: JSON.stringify({
            name: templateName,
            description: description || undefined,
            triggerType,
            editorContent,
          }),
        });
        lastSavedRef.current = editorContent;
        setDirty(false);
        clearDraft();
        setSavedId(created.id);
        navigate(`/templates/editor/${created.id}/edit`, { replace: true });
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }, [editor, savedId, templateName, description, triggerType, request, navigate, clearDraft]);

  /** Discard unsaved changes: back to the last saved version. */
  const handleDiscard = useCallback(() => {
    if (!editor) return;
    if (lastSavedRef.current) editor.commands.setContent(lastSavedRef.current as any);
    setDirty(false);
    clearDraft();
  }, [editor, clearDraft]);

  /** Leaving with the back arrow: ask first if there are unsaved changes. */
  const handleBack = useCallback(() => {
    if (dirty && !window.confirm('You have unsaved changes. Leave without saving?')) return;
    navigate('/');
  }, [dirty, navigate]);

  // ---------------------------------------------------------------------------
  // Preview
  // ---------------------------------------------------------------------------

  const handlePreview = useCallback(async () => {
    if (!editor) return;
    setPreviewing(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch('/api/templates/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ editorContent: editor.getJSON() }),
      });
      if (!res.ok) throw new Error(`Preview failed: ${res.status}`);
      const blob = await res.blob();
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
      setShowPreview(true);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setPreviewing(false);
    }
  }, [editor, getToken, previewUrl]);

  // ---------------------------------------------------------------------------
  // Image upload
  // ---------------------------------------------------------------------------

  const handleImageUpload = useCallback(async (file: File) => {
    if (!editor) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { url } = await upload<{ url: string }>('/templates/images', fd);
      // Start at logo size; the "Image size" buttons change it.
      editor.chain().focus().setImage({ src: url, width: 200 } as any).run();
    } catch (e: any) {
      setError(`Image upload failed: ${e.message}`);
    }
  }, [editor, upload]);

  if (!editor) return <Page title="Editor"><InlineStack align="center"><Spinner /></InlineStack></Page>;

  // ---------------------------------------------------------------------------
  // Toolbar
  // ---------------------------------------------------------------------------

  const tb = editor; // shorthand

  const FONT_SIZES = ['12px', '14px', '16px', '18px', '24px'];
  const currentFontSize = (editor.getAttributes('textStyle') as any).fontSize ?? '14px';

  return (
    <Page
      title={templateName}
      backAction={{ content: 'Templates', onAction: handleBack }}
      titleMetadata={dirty ? <Badge tone="attention">Unsaved changes</Badge> : undefined}
      primaryAction={{ content: 'Save template', loading: saving, onAction: handleSave }}
      secondaryActions={[
        { content: previewing ? 'Generating…' : 'Preview PDF', onAction: handlePreview, loading: previewing },
      ]}
    >
      {/* Shopify's Save / Discard bar (shown while there are unsaved changes) */}
      {/* Hidden here: App Bridge shows these as Shopify's bar at the top of the admin. */}
      <ui-save-bar id="editor-save-bar" style={{ display: 'none' }}>
        <button {...({ variant: 'primary' } as any)} onClick={handleSave}>Save</button>
        <button onClick={handleDiscard}>Discard</button>
      </ui-save-bar>

      {error && <Banner tone="critical" onDismiss={() => setError(null)}>{error}</Banner>}
      {draftFound && (
        <Box paddingBlockEnd="300">
          <Banner
            tone="warning"
            title="You have unsaved changes from earlier"
            action={{ content: 'Restore them', onAction: () => {
              editor.commands.setContent(draftFound.content as any);
              setDirty(true);
              setDraftFound(null);
            } }}
            secondaryAction={{ content: 'Discard', onAction: clearDraft }}
          >
            <p>
              We kept a copy of changes that were not saved ({new Date(draftFound.at).toLocaleString()}).
              Restore them, then click Save template.
            </p>
          </Banner>
        </Box>
      )}

      <div style={{ display: 'flex', height: 'calc(100vh - 140px)', gap: 0, background: '#f3f4f6', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,.08)' }}>

        {/* ── LEFT: toolbar + editor canvas ──────────────────────────────── */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* Toolbar */}
          <div style={{
            background: '#fff',
            borderBottom: '2px solid #e5e7eb',
            padding: '6px 10px',
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '2px',
            rowGap: '4px',
            minHeight: '44px',
            flexShrink: 0,
          }}>
            {/* Text style */}
            <TB active={tb.isActive('bold')}      onClick={() => tb.chain().focus().toggleBold().run()}      title="Bold (Ctrl+B)"><b>B</b></TB>
            <TB active={tb.isActive('italic')}    onClick={() => tb.chain().focus().toggleItalic().run()}    title="Italic (Ctrl+I)"><em>I</em></TB>
            <TB active={tb.isActive('underline')} onClick={() => tb.chain().focus().toggleUnderline().run()} title="Underline (Ctrl+U)"><u>U</u></TB>
            <TB active={tb.isActive('strike')}    onClick={() => tb.chain().focus().toggleStrike().run()}    title="Strikethrough"><s>S</s></TB>
            <Sep />
            {/* Headings */}
            {([1,2,3] as const).map(l => (
              <TB key={l} active={tb.isActive('heading', { level: l })} onClick={() => tb.chain().focus().toggleHeading({ level: l }).run()} title={`Heading ${l}`}>H{l}</TB>
            ))}
            <TB active={tb.isActive('paragraph') && !tb.isActive('heading')} onClick={() => tb.chain().focus().setParagraph().run()} title="Paragraph">P</TB>
            <Sep />
            {/* Align */}
            <TB active={tb.isActive({ textAlign: 'left' })}   onClick={() => tb.chain().focus().setTextAlign('left').run()}   title="Align left">⬅</TB>
            <TB active={tb.isActive({ textAlign: 'center' })} onClick={() => tb.chain().focus().setTextAlign('center').run()} title="Align center">⬆</TB>
            <TB active={tb.isActive({ textAlign: 'right' })}  onClick={() => tb.chain().focus().setTextAlign('right').run()}  title="Align right">➡</TB>
            <Sep />
            {/* Lists */}
            <TB active={tb.isActive('bulletList')}  onClick={() => tb.chain().focus().toggleBulletList().run()}  title="Bullet list">•</TB>
            <TB active={tb.isActive('orderedList')} onClick={() => tb.chain().focus().toggleOrderedList().run()} title="Numbered list">1.</TB>
            <Sep />
            {/* Image */}
            <TB onClick={() => fileInputRef.current?.click()} title="Insert image">🖼</TB>
            <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={e => e.target.files?.[0] && handleImageUpload(e.target.files[0])} />
            {/* Table */}
            <TB onClick={() => tb.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} title="Insert table (then use the table tools to add rows and columns)">⊞</TB>
            <Sep />
            {/* Font size */}
            <select
              value={currentFontSize}
              onChange={e => (tb.chain().focus() as any).setFontSize(e.target.value).run()}
              style={{ fontSize: '12px', border: '1px solid #e5e7eb', borderRadius: 4, padding: '2px 4px', background: '#fff', cursor: 'pointer' }}
              title="Font size"
            >
              {FONT_SIZES.map(s => <option key={s} value={s}>{s.replace('px', 'pt')}</option>)}
            </select>
            <Sep />
            {/* History */}
            <TB disabled={!tb.can().undo()} onClick={() => tb.chain().focus().undo().run()} title="Undo (Ctrl+Z)">↩</TB>
            <TB disabled={!tb.can().redo()} onClick={() => tb.chain().focus().redo().run()} title="Redo (Ctrl+Y)">↪</TB>
            {/* Page break */}
            <Sep />
            <TB onClick={() => (tb.chain().focus() as any).insertPageBreak().run()} title="Insert page break — the text after it starts on a new page">⤓ Page break</TB>
            <TB onClick={() => tb.chain().focus().wrapIn('conditionalSection').run()} title="Put the selected text, tables and fields in a section that only applies when a checkbox is ticked">▣ Conditional section</TB>
          </div>

          {/* Field tools: shown while a field is selected */}
          {selectedField && (
            <div style={{
              background: '#f9fafb', borderBottom: '1px solid #e5e7eb', padding: '5px 10px',
              display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px 4px', fontSize: 12, flexShrink: 0,
            }}>
              <span style={{ color: '#6b7280', fontWeight: 600, marginRight: 2 }}>This field:</span>
              <FieldNameInput
                key={selectedField.fieldId ?? 'field'}
                value={selectedField.label}
                onCommit={(label) => tb.chain().updateAttributes('signatureField', { label }).run()}
              />
              {isSwitch ? (
                <span style={{ color: '#6d28d9', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 4, padding: '3px 8px' }}
                  title="Signers who don't need that part simply leave this box unticked">
                  Optional — it switches a section on
                </span>
              ) : (
                <>
                  <TT active={selectedField.required} onClick={() => tb.chain().focus().updateAttributes('signatureField', { required: true }).run()}>Required</TT>
                  <TT active={!selectedField.required} onClick={() => tb.chain().focus().updateAttributes('signatureField', { required: false }).run()}>Optional</TT>
                </>
              )}
              <span style={{ color: '#6b7280', fontWeight: 600, margin: '0 2px 0 8px' }}>Show:</span>
              <select
                aria-label="When this field is shown"
                value={selectedField.showIf ?? ''}
                onChange={(e) => tb.chain().focus().updateAttributes('signatureField', { showIf: e.target.value || null }).run()}
                style={{ height: 26, fontSize: 12, border: '1px solid #e5e7eb', borderRadius: 4, maxWidth: 320 }}
              >
                <option value="">{selectedField.inSection ? 'Same as its section' : 'Always'}</option>
                {checkboxChoices.map((c) => (
                  <option key={c.fieldId} value={c.fieldId}>Only if ☑ {c.name} is ticked</option>
                ))}
                {selectedField.showIf && !checkboxChoices.some((c) => c.fieldId === selectedField.showIf) && (
                  <option value={selectedField.showIf}>Only if a removed checkbox is ticked — choose again</option>
                )}
              </select>
              {checkboxChoices.length === 0 && !selectedField.showIf && (
                <span style={{ color: '#9ca3af' }}>Add a checkbox to show this only when it's ticked.</span>
              )}
            </div>
          )}

          {/* Image size tools: shown while an image is selected */}
          {onImage && (
            <div style={{
              background: '#f9fafb', borderBottom: '1px solid #e5e7eb', padding: '4px 10px',
              display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px', fontSize: 12, flexShrink: 0,
            }}>
              <span style={{ color: '#6b7280', fontWeight: 600, marginRight: 6 }}>Image size:</span>
              {([['Small', 120], ['Medium', 240], ['Large', 400], ['Full width', null]] as const).map(([label, w]) => (
                <TT key={label} onClick={() => tb.chain().focus().updateAttributes('image', { width: w, height: null }).run()}>{label}</TT>
              ))}
            </div>
          )}

          {/* Table tools: shown while the cursor is inside a table */}
          {inTable && (
            <div style={{
              background: '#f9fafb', borderBottom: '1px solid #e5e7eb', padding: '4px 10px',
              display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px', fontSize: 12, flexShrink: 0,
            }}>
              <span style={{ color: '#6b7280', fontWeight: 600, marginRight: 6 }}>Table:</span>
              <TT onClick={() => tb.chain().focus().addRowBefore().run()}>+ Row above</TT>
              <TT onClick={() => tb.chain().focus().addRowAfter().run()}>+ Row below</TT>
              <TT onClick={() => tb.chain().focus().addColumnBefore().run()}>+ Column left</TT>
              <TT onClick={() => tb.chain().focus().addColumnAfter().run()}>+ Column right</TT>
              <Sep />
              <TT onClick={() => tb.chain().focus().deleteRow().run()}>Delete row</TT>
              <TT onClick={() => tb.chain().focus().deleteColumn().run()}>Delete column</TT>
              <Sep />
              <TT disabled={!tb.can().mergeCells()} onClick={() => tb.chain().focus().mergeCells().run()} title="Select several cells first">Merge cells</TT>
              <TT disabled={!tb.can().splitCell()} onClick={() => tb.chain().focus().splitCell().run()}>Split cell</TT>
              <TT onClick={() => tb.chain().focus().toggleHeaderRow().run()}>Header row</TT>
              <Sep />
              <TT danger onClick={() => tb.chain().focus().deleteTable().run()}>Delete table</TT>
            </div>
          )}

          {/* A4 canvas */}
          <div style={{ flex: 1, overflow: 'auto', padding: '28px 40px', display: 'flex', justifyContent: 'center', background: '#e8eaed' }}>
            <div style={{
              background: '#fff', width: '800px', minHeight: '1131px',
              boxShadow: '0 2px 12px rgba(0,0,0,.18)', borderRadius: '2px', position: 'relative',
            }}>
              <EditorContent editor={editor} />
            </div>
          </div>
        </div>

        {/* ── RIGHT: insert field sidebar ────────────────────────────────── */}
        <div style={{ width: 200, background: '#fff', borderLeft: '1px solid #e5e7eb', overflowY: 'auto', flexShrink: 0 }}>
          <div style={{ padding: '14px 12px' }}>
            <Text variant="headingSm" as="h3">Insert Field</Text>
            <Text variant="bodySm" tone="subdued" as="p">Click to place at cursor</Text>
            {signers.length > 1 && (
              <div style={{ marginTop: 10 }}>
                <Select
                  label="Fields for"
                  options={signers.map((s) => ({ label: `${s.position}. ${s.label}`, value: String(s.position) }))}
                  value={String(insertFor)}
                  onChange={(v) => setInsertFor(Number(v))}
                />
              </div>
            )}
          </div>
          <Divider />

          <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {([
              { type: 'SIGNATURE', label: '✍ Signature' },
              { type: 'INITIALS',  label: 'AB Initials' },
              { type: 'NAME',      label: '👤 Name' },
              { type: 'EMAIL',     label: '@ Email' },
              { type: 'DATE',      label: '📅 Date' },
              { type: 'CHECKBOX',  label: '☑ Checkbox' },
              { type: 'TEXT',      label: 'T  Text' },
            ] as const).map(({ type, label }) => {
              const color = FIELD_COLORS[type];
              return (
                <button
                  key={type}
                  onClick={() => insertForSigner.position > 1
                    ? insertSignatureField(editor, type, `${insertForSigner.label} – ${label.replace(/^\S+\s+/, '')}`, insertForSigner.position)
                    : insertSignatureField(editor, type)}
                  style={{
                    padding: '7px 10px', border: `1.5px solid ${color}55`,
                    borderRadius: 6, background: `${color}0e`, color,
                    fontSize: 12, fontWeight: 600, cursor: 'pointer', textAlign: 'left',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = `${color}22`)}
                  onMouseLeave={e => (e.currentTarget.style.background = `${color}0e`)}
                >
                  {label}
                </button>
              );
            })}
          </div>

          <Divider />
          <div style={{ padding: '12px 12px 4px' }}>
            <InlineStack align="space-between">
              <Text variant="headingSm" as="h4">Fields placed</Text>
              <Badge>{fieldCount.toString()}</Badge>
            </InlineStack>
          </div>
          <div style={{ padding: '4px 12px 12px', fontSize: 11, color: '#6b7280' }}>
            {fieldCount === 0
              ? 'No fields yet — click above to add'
              : `${fieldCount} field${fieldCount !== 1 ? 's' : ''} will auto-position in the PDF`}
          </div>
        </div>
      </div>

      {/* ── Library selection modal ─────────────────────────────────────── */}
      <Modal
        open={showLibrary}
        onClose={() => setShowLibrary(false)}
        title="Choose a starting point"
        primaryAction={{ content: 'Start blank', onAction: () => setShowLibrary(false) }}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Text as="p" variant="bodyMd" tone="subdued">
              Pick a pre-built template to customize, or start from a blank document.
            </Text>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              {library.map(tmpl => (
                <button
                  key={tmpl.slug}
                  onClick={() => loadLibraryTemplate(tmpl.slug)}
                  style={{
                    border: '1.5px solid #e5e7eb', borderRadius: 8, padding: '12px 14px',
                    background: '#fafafa', cursor: 'pointer', textAlign: 'left',
                    transition: 'border-color 0.15s',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.borderColor = '#2563EB')}
                  onMouseLeave={e => (e.currentTarget.style.borderColor = '#e5e7eb')}
                >
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{tmpl.name}</div>
                  <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6 }}>{tmpl.description}</div>
                  <Badge tone="info">{`${tmpl.fieldCount} fields`}</Badge>
                </button>
              ))}
            </div>
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* ── PDF Preview modal ───────────────────────────────────────────── */}
      {showPreview && previewUrl && (
        <Modal
          open={showPreview}
          onClose={() => setShowPreview(false)}
          title="PDF Preview"
          size="large"
          primaryAction={{ content: 'Close', onAction: () => setShowPreview(false) }}
        >
          <Modal.Section>
            <div style={{ display: 'flex', justifyContent: 'center', background: '#f3f4f6', padding: '20px', borderRadius: 8 }}>
              <Document
                file={previewUrl}
                onLoadSuccess={({ numPages }) => setNumPreviewPages(numPages)}
              >
                {Array.from({ length: numPreviewPages }, (_, i) => (
                  <div key={i} style={{ marginBottom: 12 }}>
                    <PdfPage
                      pageNumber={i + 1}
                      width={600}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  </div>
                ))}
              </Document>
            </div>
          </Modal.Section>
        </Modal>
      )}
    </Page>
  );
}
